import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Dropzone — drag & drop + click-to-browse for PDFs.
 * Props:
 *   onFiles(fileList)        : callback with File[] after selection/drop
 *   maxFiles (number)        : cap the number of files accepted at once
 *   maxFileBytes (number)    : cap the size per file
 *   disabled (bool)
 */
export default function Dropzone({
  onFiles,
  maxFiles = 5,
  maxFileBytes = 100 * 1024 * 1024,
  disabled = false,
}) {
  const [hover, setHover] = useState(false);
  const inputRef = useRef(null);

  const filterFiles = useCallback(
    (list) => {
      const arr = Array.from(list || []);
      const valid = [];
      const rejected = [];
      for (const f of arr) {
        if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
          rejected.push({ file: f, reason: "non-PDF" });
          continue;
        }
        if (f.size > maxFileBytes) {
          rejected.push({
            file: f,
            reason: `> ${Math.round(maxFileBytes / 1024 / 1024)} Mo`,
          });
          continue;
        }
        valid.push(f);
      }
      if (valid.length > maxFiles) {
        rejected.push(
          ...valid.slice(maxFiles).map((f) => ({ file: f, reason: "> 5 fichiers" }))
        );
        return { valid: valid.slice(0, maxFiles), rejected };
      }
      return { valid, rejected };
    },
    [maxFiles, maxFileBytes]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHover(false);
    if (disabled) return;
    const { valid, rejected } = filterFiles(e.dataTransfer?.files);
    onFiles?.(valid, rejected);
  };

  const handleBrowse = (e) => {
    const { valid, rejected } = filterFiles(e.target.files);
    onFiles?.(valid, rejected);
    e.target.value = "";
  };

  return (
    <div
      data-testid="dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={handleDrop}
      className={cn(
        "relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-all",
        hover
          ? "border-brand-gold bg-accent/40"
          : "border-border bg-card/40 hover:border-brand-gold/60 hover:bg-accent/20",
        disabled && "cursor-not-allowed opacity-60"
      )}
      onClick={() => !disabled && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={handleBrowse}
        disabled={disabled}
        data-testid="dropzone-input"
      />
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent/50">
        <UploadCloud className="h-7 w-7 text-brand-gold" />
      </div>
      <div className="text-base font-semibold">
        Déposez vos PDF ici, ou cliquez pour parcourir
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Max {maxFiles} fichiers · 100 Mo chacun · format PDF uniquement
      </div>
      <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1 text-[11px] text-muted-foreground">
        <FileText className="h-3.5 w-3.5" />
        Lois de finances, budgets, rapports d'audit, textes juridiques…
      </div>
    </div>
  );
}
